export const DocumentType = {
  TRANSFER_CERTIFICATE: 'TRANSFER_CERTIFICATE',
  MEDICAL_VISIT_ATTESTATION: 'MEDICAL_VISIT_ATTESTATION',
  HABILITATION_ATTESTATION: 'HABILITATION_ATTESTATION',
  COMPLIANCE_RECORD: 'COMPLIANCE_RECORD',
  HABILITATION_TITLE: 'HABILITATION_TITLE',
  PPE_ISSUE_RECORD: 'PPE_ISSUE_RECORD',
  MEDICAL_FOLLOWUP_RECORD: 'MEDICAL_FOLLOWUP_RECORD',
} as const;

export type DocumentType = (typeof DocumentType)[keyof typeof DocumentType];

/**
 * The only types a human triggers from a user's profile. Every other type is a
 * side effect of something else (a transfer being accepted, a medical visit or
 * habilitation being recorded with its supporting file).
 *
 * The array order is the order of the "generate a document" menu, and it is the
 * single source both the menu and the request DTO read from — adding a fourth
 * document means one entry here, not a new list to keep in sync.
 */
export const onDemandUserDocumentTypes = [
  DocumentType.COMPLIANCE_RECORD,
  DocumentType.HABILITATION_TITLE,
  DocumentType.PPE_ISSUE_RECORD,
  DocumentType.MEDICAL_FOLLOWUP_RECORD,
] as const;

/**
 * Occupational-health documents, gated on `medicalVisit.list` on top of
 * `userDocument.generate`: the right to produce a document about someone is not
 * the right to read their health follow-up. Kept here rather than in the
 * controller so the menu and the API read the same list.
 */
export const medicalUserDocumentTypes = [
  DocumentType.MEDICAL_FOLLOWUP_RECORD,
] as const;

export const isMedicalUserDocumentType = (
  documentType: DocumentType,
): boolean =>
  medicalUserDocumentTypes.some((medical) => medical === documentType);

export type OnDemandUserDocumentType =
  (typeof onDemandUserDocumentTypes)[number];

export const UserDocumentStatus = {
  PENDING: 'PENDING',
  GENERATED: 'GENERATED',
  FAILED: 'FAILED',
} as const;

export type UserDocumentStatus =
  (typeof UserDocumentStatus)[keyof typeof UserDocumentStatus];

export type UserDocument = {
  id: number;
  createdAt: Date | string;
  documentType: DocumentType;
  status: UserDocumentStatus;
  generatedAt?: Date | string | null;
  transferId?: number | null;
  medicalVisitId?: number | null;
  habilitationId?: number | null;
  targetUserId?: number | null;
  generatedByUserId?: number | null;
  generatedByUser?: {
    id: number;
    firstName: string;
    lastName: string;
  } | null;
};

/**
 * A file on the private R2 bucket (never a public href — storageKey/storageBucket
 * stay backend-only, only ever reachable through a short-lived presigned URL).
 * Shared by UserDocument (system-generated PDFs and user uploads targeting a
 * single user) and OrgDocument (broadcast to N users via folder ACL) — all
 * are sensitive documents on the same bucket.
 */
export type PrivateFile = {
  id: number;
  createdAt: Date | string;
  name: string;
  mimeType: string;
  fileSizeBytes: number;
};
